import { isValidElement, memo, useEffect, useLayoutEffect, type MouseEvent } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { Button, HighlightLogo, Interactive } from '@highlight/ui'
import HighlightLogoIcon from '@renderer/components/Icons/HighlightLogoIcon'
import { toastManager } from '@renderer/overlay/Toaster/toast-store'
import type { ToastItem } from '@renderer/overlay/Toaster/toast-store'
import { trackNotchToastClicked, trackNotchToastShown } from '../../../track-notch'
import { notchCrossfadeTransition, notchCrossfadeVariants, notchResizeTransition } from './notch-motion'
import { useElementSize } from './use-element-size'

export const NOTCH_TOAST_WIDTH = 330
export const NOTCH_TOAST_RADIUS = 16
/** The design's wide face: a row toast's one line needs the extra room, under panel corners. */
export const NOTCH_TOAST_ROW_WIDTH = 360
export const NOTCH_TOAST_ROW_RADIUS = 20
const NOTCH_TOAST_HEADER_HEIGHT = 44
const NOTCH_TOAST_ROW_GAP = 6

type ContentClickHandler = (event: MouseEvent<HTMLElement>) => void

interface NotchToastProps {
  onHeightChange: (height: number) => void
  onLogoClick: () => void
  toast: ToastItem
  /** The cutout's top row, which a row toast hangs below. */
  topInset: number
}

/**
 * Rendered at its final width so text never reflows while the cutout grows around it. Reports its
 * resting height up front so the cutout springs straight to it instead of chasing each frame.
 */
export const NotchToast = memo(function NotchToast({ onHeightChange, onLogoClick, toast, topInset }: NotchToastProps) {
  useEffect(() => trackNotchToastShown(toast), [toast])

  // Capture, so the click is counted before a control that dismisses the toast removes it
  const handleContentClick: ContentClickHandler = (event) => {
    const control = (event.target as Element).closest<HTMLElement>('button, a, [role="button"]')
    if (!control || !event.currentTarget.contains(control)) return
    const row = control.closest<HTMLElement>('[data-staged-action-id]')
    trackNotchToastClicked(toast, control.dataset.notchControl ?? 'unnamed', row ? stagedActionRowProperties(row) : {})
  }

  return toast.notchRow ? (
    <NotchToastRowLayout
      toast={toast}
      topInset={topInset}
      onHeightChange={onHeightChange}
      onContentClick={handleContentClick}
    />
  ) : (
    <NotchToastCard
      toast={toast}
      onHeightChange={onHeightChange}
      onLogoClick={onLogoClick}
      onContentClick={handleContentClick}
    />
  )
})

interface NotchToastRowLayoutProps {
  toast: ToastItem
  topInset: number
  onHeightChange: (height: number) => void
  onContentClick: ContentClickHandler
}

/** One clickable row under the cutout's top, with no header of its own. */
function NotchToastRowLayout({ toast, topInset, onHeightChange, onContentClick }: NotchToastRowLayoutProps) {
  const [rootRef, rootSize] = useElementSize<HTMLDivElement>()

  useLayoutEffect(() => {
    if (rootSize.height > 0) {
      onHeightChange(rootSize.height)
    }
  }, [rootSize.height, onHeightChange])

  return (
    <div
      ref={rootRef}
      className="text-foreground dark px-1 pb-1"
      style={{ width: NOTCH_TOAST_ROW_WIDTH, paddingTop: topInset + NOTCH_TOAST_ROW_GAP }}
    >
      <Interactive>
        <div className="relative" aria-live="polite" onClickCapture={onContentClick}>
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              key={toast.instanceId}
              variants={notchCrossfadeVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={notchCrossfadeTransition}
            >
              {toast.notchRow}
            </motion.div>
          </AnimatePresence>
        </div>
      </Interactive>
    </div>
  )
}

interface NotchToastCardProps {
  toast: ToastItem
  onHeightChange: (height: number) => void
  onLogoClick: () => void
  onContentClick: ContentClickHandler
}

/** The notch's header, with Highlight's logo and a dismiss, over the toast's body. */
function NotchToastCard({ toast, onHeightChange, onLogoClick, onContentClick }: NotchToastCardProps) {
  const [contentRef, contentSize] = useElementSize<HTMLDivElement>()

  useLayoutEffect(() => {
    if (contentSize.height > 0) {
      onHeightChange(NOTCH_TOAST_HEADER_HEIGHT + contentSize.height)
    }
  }, [contentSize.height, onHeightChange])

  return (
    <div className="flex flex-col" style={{ width: NOTCH_TOAST_WIDTH }}>
      <div className="flex items-center justify-between px-1" style={{ height: NOTCH_TOAST_HEADER_HEIGHT }}>
        <Interactive>
          <Button
            aria-label="Open Highlight"
            variant="ghost"
            shape="square"
            tone="on-dark"
            onClick={() => {
              trackNotchToastClicked(toast, 'open highlight')
              onLogoClick()
            }}
          >
            <HighlightLogo size={20}>
              <HighlightLogo.Icon />
            </HighlightLogo>
          </Button>
        </Interactive>

        <Interactive>
          <Button
            aria-label="Dismiss notification"
            variant="ghost"
            shape="square"
            tone="on-dark"
            className="group/dismiss"
            onClick={() => toastManager.dismiss(toast.id, 'dismiss button')}
          >
            <X
              aria-hidden="true"
              className="size-5 opacity-40 transition-opacity group-hover/dismiss:opacity-70"
              strokeWidth={1.5}
            />
          </Button>
        </Interactive>
      </div>

      <Interactive>
        <motion.div
          className="border-white-4 relative overflow-hidden border-t"
          initial={false}
          animate={{ height: contentSize.height || 'auto' }}
          transition={notchResizeTransition}
          aria-live="polite"
          onClickCapture={onContentClick}
        >
          <div ref={contentRef} className="relative">
            <AnimatePresence initial={false} mode="popLayout">
              <motion.div
                key={toast.instanceId}
                className="flex flex-col p-3"
                variants={notchCrossfadeVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={notchCrossfadeTransition}
              >
                {toast.notchDescription ?? <NotchToastFallback toast={toast} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </Interactive>
    </div>
  )
}

/** A toast with no notch body: its icon and title, then its corner body, themed dark to sit on the notch. */
function NotchToastFallback({ toast }: { toast: ToastItem }) {
  // The notch header already shows the Highlight logo
  const icon = isValidElement(toast.icon) && toast.icon.type === HighlightLogoIcon ? null : toast.icon
  const header = (
    <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-white">
      {icon && <span className="flex size-4 shrink-0 items-center justify-center">{icon}</span>}
      <span className="truncate">{toast.title}</span>
    </span>
  )

  return (
    <div className="text-foreground dark flex max-h-[320px] flex-col gap-2 overflow-y-auto">
      {toast.title &&
        !toast.descriptionHasHeader &&
        (toast.onHeaderClick ? (
          <button
            type="button"
            data-notch-control="header"
            onClick={toast.onHeaderClick}
            className="min-w-0 max-w-full cursor-pointer bg-transparent p-0 text-left"
          >
            {header}
          </button>
        ) : (
          header
        ))}
      {toast.description}
    </div>
  )
}

/** The staged action a clicked row stands for, from the `data-staged-action-*` attributes its row carries. */
function stagedActionRowProperties(row: HTMLElement): Record<string, unknown> {
  const { stagedActionId, stagedActionToolName, stagedActionToolSet, stagedActionPlanner, stagedActionStatus } =
    row.dataset
  return {
    actionId: stagedActionId,
    toolName: stagedActionToolName,
    toolSet: stagedActionToolSet,
    planner: stagedActionPlanner,
    actionStatus: stagedActionStatus,
  }
}
